<template>
    <div
        v-if="eventData"
        class="d-flex align-items-center flex-column mx-3 w-full mx-auto p-4"
        style="max-width: 900px;">
        <notifications />
        <div
            id="successDisplayCard"
            class="card w-100">
            <div class="card-header">
                <h2 class="mb-0">
                    Ihre Anmeldung
                </h2>
            </div>
            <div class="card-body d-flex flex-sm-row flex-column gap-4 gap-sm-5 px-4 px-sm-5 align-items-center">
                <font-awesome-icon
                    icon="check"
                    size="6x"
                    class="text-success" />
                <div
                    v-if="groupRegistrationCreationMode"
                    class="d-flex align-items-sm-start flex-column align-items-center">
                    <h2 class="mb-0">
                        {{ $t('eventRegistration.startOfGroupRegistrationSuccessful') }}
                    </h2>
                    <a :href="groupRegistrarLink">
                        <base-button class="mt-3">{{ $t('eventRegistration.editGroupRegistration') }}</base-button>
                    </a>
                </div>
                <div
                    v-else
                    class="d-flex align-items-sm-start flex-column align-items-center">
                    <h2 class="mt-2 text-center">
                        <span v-if="isGroupRegistrar && !registrationData.finishGroupRegistration">
                            {{ $t('eventRegistration.groupRegistrationSavedSuccessful') }}
                        </span>
                        <span v-else>
                            {{ $t('eventRegistration.registrationSuccessful') }}
                        </span>
                    </h2>
                    <base-button @click="showContractualInfo(true)">
                        {{ $t('eventRegistration.openPrintView') }}
                    </base-button>
                </div>
            </div>
        </div>
        <div class="card w-100">
            <div class="card-header">
                <h2 class="mb-0">
                    {{ $t('eventRegistration.registrationState') }}
                </h2>
            </div>
            <div class="card-body">
                <div
                    v-if="registrationData.id"
                    class="text-center mb-4">
                    {{ $t('eventRegistration.yourRegistrationNumber') }}: {{ registrationData.id }}
                </div>
                <div
                    class="text-center mb-4">
                    <strong
                        v-if="registrationData.requestId">
                        {{ $t('eventRegistration.yourProcessNumber') }}: {{ registrationData.requestId }}
                    </strong>
                </div>
                <div class="text-center">
                    <BaseCallout
                        v-if="registrationData.requestFailure"
                        type="danger"
                        icon="exclamation-triangle"
                        style="max-width: 600px">
                        <div
                            class="text-left d-flex flex-column gap-2">
                            <span>
                                {{ $t('eventRegistration.requestFailed') }}
                            </span>
                            <span
                                v-if="failureMessage"
                                class="text-sm">
                                <font-awesome-icon
                                    :icon="['far', 'comment']"
                                    size="sm" />
                                <i>{{ failureMessage }}</i>
                            </span>
                        </div>
                    </BaseCallout>
                    <div
                        v-else-if="registrationData.requestId"
                        style="max-width: 800px"
                        class="d-flex flex-column gap-2">
                        <span>
                            <DefaultSpinner class="text-muted" />
                            {{ $t('eventRegistration.requestBeingProcessed') }}
                        </span>
                        <small class="text-muted">
                            {{ $t('eventRegistration.pleaseWait') }}
                        </small>
                    </div>
                    <span v-else-if="registrationData.id">
                        <font-awesome-icon icon="check" />
                        {{ $t('eventRegistration.requestProcessed') }}
                    </span>
                    <span v-else>
                        <font-awesome-icon icon="question-circle" />
                        {{ $t('eventRegistration.requestNotFound') }}
                    </span>
                </div>
            </div>
        </div>
        <div class="mt--4 d-flex justify-content-end text-xs text-muted w-100 gap-1">
            <a
                class="d-inline-flex gap-1 text-primary"
                href="https://www.amosweb.de"
                target="_blank">

                <span>powered by</span>
                <img
                    src="/icon.png"
                    style="width: 3.125rem; height: auto"
                    alt="Sidebar logo">
            </a>
        </div>
        <div
            v-if="eventData?.advertisedNewsletter"
            class="card w-100">
            <div class="card-header">
                <h2 class="mb-0">
                    {{ $t('eventRegistration.summary.newsletter') }}
                </h2>
            </div>
            <div class="card-body">
                <NewsletterAdvertisement />
            </div>
        </div>
        <div
            v-if="eventData?.advertisedProjects?.length"
            class="card w-100">
            <div class="card-header">
                <h2 class="mb-0">
                    {{ $t('eventRegistration.summary.projects') }}
                </h2>
            </div>
        </div>
        <div
            v-if="eventData.advertisedProjects?.length"
            class="w-100 d-flex flex-column  gap-4">
            <ProjectOverview
                v-for="project in eventData.advertisedProjects"
                :key="project.id"
                :project="project.advertisable" />
        </div>
    </div>
</template>

<script>
import mainRegistrationMixin from "@/mixins/registration/mainRegistrationMixin";
import registrationGroupMixin from "@/mixins/registration/registrationGroupMixin";
import contractualInfoMixin from "@/mixins/registration/contractualInfoMixin";

const registeredErrorMessages = {
    alreadyRegistered: true,
    paypalUnauthorized: true,
    questionAnswerNotAvailable: "name",
    quantityAnswerNotAvailable: "name",
    groupRegistrarTokenMismatch: false,
    groupParticipantTokenMismatch: false,
    caregiverTokenMismatch: false,
    noCheckTokenTokenMismatch: false,
    notEnoughPlacesForFemaleParticipantsLeft: false,
    notEnoughPlacesForMaleParticipantsLeft: false,
    notEnoughPlacesForParticipantsLeft: false,
    notEnoughPlacesLeft: false,
    invalidIban: false,
    invalidCaregiverUuid: false,
    alreadyRedeemedCouponCode: false,
    incompatibleCouponCode: false,
    wrongCouponCode: false,
    invalidPaymentType: false,
    not_authorized: false,
    participantCanNotBeDeleted: "name",
    registrationTransactionError: false,
    blockedByAsyncRequest: false
}

export default {
    name: "SuccessFinishPage",
    mixins: [mainRegistrationMixin, registrationGroupMixin, contractualInfoMixin],
    data() {
        return {
            active: false,
            currentTimeout: null,
            timeout: 2000
        }
    },
    computed: {
        failureMessage() {
            const errorType = registeredErrorMessages[this.registrationData?.requestFailure?.message] ?? null
            if (!this.registrationData?.requestFailure?.message || errorType === null) return null
            let message = this.$t('error.' + this.registrationData.requestFailure?.message)
            if (errorType === true)
                message = this.$t('error.' + this.registrationData.requestFailure?.message) + " " + this.registrationData.requestFailure.additionalData
            else if (errorType)
                message = this.$t('error.' + this.registrationData.requestFailure?.message, {[errorType]: this.registrationData.requestFailure.additionalData})

            return message
        }
    }
    ,
    methods: {
        scheduleNextLoadState() {
            clearTimeout(this.currentTimeout)
            this.setActiveState()
            this.updateTimeout()
            if (this.active) this.currentTimeout = setTimeout(this.checkRequestState, this.timeout)
        },
        async checkRequestState() {
            await this.$store.dispatch('registration/checkRequestState', {eventId: parseInt(this.$route.params.id)})
            this.scheduleNextLoadState()
        },
        setActiveState() {
            this.active = this.registrationData?.requestId && !this.registrationData?.requestFailure
        },
        updateTimeout() {
            if (this.timeout < 5000)
                this.timeout += 1000
            else if (this.timeout < 10000)
                this.timeout *= 1.01
            else
                this.timeout = 10000
        },
        async showContractualInfo() {
            const ref = this.openNewLoadingWindow();
            try {
                const response = await this.$axios.post(this.eventData.printViewUrl, this.registrationData, {responseType: 'blob'})
                let filename = response.headers['content-disposition'].match(/filename="(.+?)"/)[1] ?? 'Anmeldungsübersicht.pdf';
                if (response.headers['content-type'] !== 'application/pdf') {
                    filename = filename.replace(".pdf", ".docx")
                }
                const url = URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
            } catch (e) {
                this.$notify({
                    type: 'danger',
                    message: this.$t(e.response?.status === 404 ? 'error.registrationNotFound' : 'error.printViewFailed'),
                    timeout: 15000
                })
            }

            ref?.close()
        }
    },
    mounted() {
        // scroll to top
        this.$nextTick(() => {
            document.getElementById('successDisplayCard')?.scrollIntoView({behavior: 'smooth', block: 'start'})
        })

        if (this.registrationData?.requestId)
            this.active = true
        this.scheduleNextLoadState()
    },
    deactivated() {
        this.active = false
    }
}
</script>

<style scoped>

</style>
